// src/components/ui/toaster.tsx
import * as React from "react";
import * as Toast from "@radix-ui/react-toast";

type ToastMessage = { id: number; title?: string; description?: string; duration?: number };

let _push: ((msg: Omit<ToastMessage, "id">) => void) | null = null;

export function toast(message: string, opts?: { description?: string; duration?: number }) {
  _push?.({ title: message, description: opts?.description, duration: opts?.duration });
}

export function Toaster() {
  const [open, setOpen] = React.useState(false);
  const [queue, setQueue] = React.useState<ToastMessage[]>([]);
  const [active, setActive] = React.useState<ToastMessage | null>(null);
  const idRef = React.useRef(1);

  React.useEffect(() => {
    _push = (msg) => setQueue((q) => [...q, { id: idRef.current++, ...msg }]);
    return () => {
      _push = null;
    };
  }, []);

  React.useEffect(() => {
    if (!active && queue.length > 0) {
      const [next, ...rest] = queue;
      setActive(next);
      setQueue(rest);
      setOpen(true);
    }
  }, [queue, active]);

  return (
    <Toast.Provider swipeDirection="right">
      <Toast.Root
        open={open}
        onOpenChange={setOpen}
        duration={active?.duration ?? 3000}
        className="pointer-events-auto fixed bottom-6 right-6 z-50 rounded-lg border bg-white/95 p-4 shadow-xl backdrop-blur-sm dark:border-neutral-800 dark:bg-neutral-900/95"
      >
        {active?.title && (
          <Toast.Title className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {active.title}
          </Toast.Title>
        )}
        {active?.description && (
          <Toast.Description className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
            {active.description}
          </Toast.Description>
        )}
        <Toast.Close
          aria-label="Close"
          className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 focus:outline-none dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
        >
          ×
        </Toast.Close>
      </Toast.Root>

      <Toast.Viewport className="pointer-events-none fixed bottom-0 right-0 z-50 m-0 flex w-[420px] max-w-[100vw] list-none flex-col gap-2 p-6 outline-none" />
    </Toast.Provider>
  );
}
